import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Search, Plus, KeyRound, ChevronRight, Trophy,
  Users, IdCard, Activity,
} from "lucide-react";
import { toast } from "sonner";
import { WEST_AFRICA_COUNTRIES } from "../lib/fcfa";

// Benin national colors: green #008751 (vertical band), yellow #FCD116, red #E8112D
// Use deep green background with subtle diamond pattern + emerald gradient logo card

const DIAMOND_BG = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'><path d='M20 0 L40 20 L20 40 L0 20 Z' fill='none' stroke='%23ffffff' stroke-width='0.6' opacity='0.05'/></svg>`
)}`;

const QUICK_TILES = [
  { icon: Users, label: "Ressources Humaines", color: "#E8112D" },
  { icon: IdCard, label: "Cartes Membres", color: "#FCD116" },
  { icon: Activity, label: "Performances", color: "#FCD116" },
];

export default function ClubGate() {
  const navigate = useNavigate();
  const { login, registerClub } = useAuth();

  const [clubs, setClubs] = useState([]);
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [pickedClub, setPickedClub] = useState(null);
  const [codeOpen, setCodeOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const [regForm, setRegForm] = useState({
    name: "", city: "", country: "Bénin", sport: "Football",
    admin_code: "", president_label: "Président",
  });

  // Search clubs only when user types ≥ 2 chars (no enumeration of clubs)
  useEffect(() => {
    if (!search || search.trim().length < 2) {
      setClubs([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      api.get(`/clubs/public`, { params: { q: search.trim() } })
        .then(({ data }) => setClubs(data))
        .catch(() => setClubs([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const grouped = useMemo(() => {
    return [...clubs].sort((a, b) => (a.name || "").localeCompare(b.name));
  }, [clubs]);

  const handlePickClub = (club) => {
    setPickedClub(club);
    setCode("");
    setCodeOpen(true);
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    if (!pickedClub) return;
    setLoading(true);
    try {
      await login(pickedClub.slug, code);
      toast.success(`Connecté à ${pickedClub.name}`);
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    if (regForm.admin_code.length < 4) {
      toast.error("Code Président : 4 caractères minimum");
      return;
    }
    setLoading(true);
    try {
      await registerClub(regForm);
      toast.success("Club créé avec succès !");
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col relative"
      style={{
        background: "linear-gradient(180deg, #0A4628 0%, #0F3D1F 45%, #082C18 100%)",
      }}
    >
      {/* Diamond pattern overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url("${DIAMOND_BG}")`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Yellow + Red vertical bands (Benin flag accent) */}
      <div className="absolute inset-y-0 left-0 w-2 bg-[#FCD116] z-10" />
      <div className="absolute inset-y-0 right-0 w-2 bg-[#E8112D] z-10" />

      <div className="relative z-20 flex-1 flex flex-col items-center px-4 py-10 md:py-14">
        {/* Logo crest */}
        <div className="flex flex-col items-center max-w-md w-full">
          <div className="relative w-28 h-32" data-testid="logo-crest">
            <svg viewBox="0 0 100 110" className="w-full h-full drop-shadow-2xl">
              <defs>
                <linearGradient id="shield" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#0F8B4E" />
                  <stop offset="100%" stopColor="#06371F" />
                </linearGradient>
              </defs>
              <path d="M50 5 L92 18 L92 60 Q92 88 50 105 Q8 88 8 60 L8 18 Z"
                    fill="url(#shield)" stroke="#FCD116" strokeWidth="3" />
              <text x="50" y="42" textAnchor="middle"
                    fill="#FCD116" fontFamily="Cabinet Grotesk, sans-serif"
                    fontWeight="900" fontSize="18" letterSpacing="2">WSDK</text>
              <circle cx="50" cy="65" r="14" fill="#E8112D" stroke="#FCD116" strokeWidth="2" />
              <path d="M40 65 L48 73 L60 60" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <div className="text-center mt-5">
            <p className="font-display font-bold text-[#FCD116] tracking-wider text-sm sm:text-base">
              CONNECTER &nbsp;·&nbsp; DÉVELOPPER &nbsp;·&nbsp; VALORISER
            </p>
            <p className="text-white/65 text-xs sm:text-sm mt-2 tracking-wide">
              Plateforme de Gestion des Clubs Sportifs
            </p>
          </div>
        </div>

        {/* Selection card */}
        <div
          className="w-full max-w-md mt-8 rounded-2xl border border-[#FCD116]/15 backdrop-blur-md p-6 sm:p-7"
          style={{ background: "rgba(15, 61, 31, 0.65)" }}
          data-testid="club-gate-card"
        >
          <h1 className="font-display font-extrabold text-white text-2xl sm:text-3xl text-center">
            Sélectionnez votre club
          </h1>
          <p className="text-white/55 text-sm text-center mt-2">
            Choisissez le club auquel vous appartenez
          </p>

          <div className="relative mt-5">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tapez le nom de votre club..."
              data-testid="club-search-input"
              className="pl-11 h-12 bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#FCD116]/40 focus-visible:border-[#FCD116]/40"
            />
            {searching && (
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <div className="w-3 h-3 border-2 border-[#FCD116] border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>

          <div className="mt-4 max-h-[340px] overflow-y-auto space-y-2 pr-1" data-testid="club-list">
            {search.trim().length < 2 ? (
              <div className="text-center py-10 text-white/50 text-sm">
                <Search className="w-6 h-6 mx-auto mb-3 opacity-40" />
                Tapez au moins 2 lettres du nom de votre club
                <p className="text-xs text-white/35 mt-2">Pour des raisons de confidentialité, aucune liste publique n'est affichée.</p>
              </div>
            ) : grouped.length === 0 ? (
              <div className="text-center py-8 text-white/55 text-sm">
                {searching ? "Recherche en cours..." : `Aucun club ne correspond à « ${search} ».`}
              </div>
            ) : grouped.map((c) => {
              const initial = (c.name || "?").charAt(0).toUpperCase();
              return (
                <button
                  key={c.slug}
                  onClick={() => handlePickClub(c)}
                  data-testid={`club-item-${c.slug}`}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 hover:border-[#FCD116]/50 hover:bg-white/5 transition group text-left"
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                       style={{ background: "rgba(252, 209, 22, 0.15)", color: "#FCD116" }}>
                    <span className="font-display font-bold">{initial}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-white text-sm uppercase tracking-wide truncate">
                      {c.name}
                    </p>
                    <p className="text-white/45 text-xs truncate">
                      {(c.city || "—").toUpperCase()} · Football
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-[#FCD116] transition" />
                </button>
              );
            })}
          </div>

          <div className="border-t border-white/10 mt-5 pt-5 text-center">
            <p className="text-white/55 text-sm mb-3">Votre club n'est pas encore inscrit ?</p>
            <Button
              onClick={() => navigate("/register")}
              data-testid="open-register-btn"
              className="bg-transparent hover:bg-[#FCD116] text-[#FCD116] hover:text-[#0A4628] border border-[#FCD116]/60 font-semibold transition"
            >
              <Plus className="w-4 h-4 mr-2" /> Inscrire mon club
            </Button>
          </div>
        </div>

        {/* Quick tiles */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-8 w-full max-w-md">
          {QUICK_TILES.map((t) => {
            const Icon = t.icon;
            return (
              <div
                key={t.label}
                className="rounded-xl border border-white/10 p-3 sm:p-4 text-center backdrop-blur-sm"
                style={{ background: "rgba(15, 61, 31, 0.45)" }}
                data-testid={`quick-tile-${t.label}`}
              >
                <div
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-md mx-auto mb-2 flex items-center justify-center"
                  style={{ background: `${t.color}1a`, color: t.color }}
                >
                  <Icon className="w-4 sm:w-5 h-4 sm:h-5" />
                </div>
                <p className="text-white text-[10px] sm:text-xs font-semibold leading-tight">
                  {t.label}
                </p>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <footer className="mt-auto pt-12 text-center text-white/45 text-xs space-y-1.5 max-w-md">
          <p className="font-display font-bold text-[#FCD116] tracking-wider text-sm">
            WSDK CONNECT
          </p>
          <p>© {new Date().getFullYear()} — La Plateforme Sportive Connectée d'Afrique de l'Ouest</p>
          <p className="italic text-white/30">Fait au Bénin, pour l'Afrique, Avec Passion</p>
        </footer>
      </div>

      {/* Code entry dialog */}
      <Dialog open={codeOpen} onOpenChange={setCodeOpen}>
        <DialogContent className="max-w-sm" data-testid="code-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {pickedClub?.name}
            </DialogTitle>
            <DialogDescription>
              {pickedClub?.city ? `${pickedClub.city} · ` : ""}Saisissez votre code d'accès personnel.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitLogin} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Code d'accès</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#616161]" />
                <Input
                  type="password"
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Votre code"
                  className="pl-9 h-11"
                  data-testid="gate-code-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCodeOpen(false)}>Annuler</Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#0A6B3A] hover:bg-[#08512C] text-white"
                data-testid="gate-login-btn"
              >
                {loading ? "Connexion..." : "Se connecter"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Register dialog */}
      <Dialog open={registerOpen} onOpenChange={setRegisterOpen}>
        <DialogContent className="max-w-md" data-testid="register-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Inscrire mon club</DialogTitle>
            <DialogDescription>Créez votre espace administratif en quelques secondes.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitRegister} className="space-y-3">
            <div>
              <Label>Nom du club *</Label>
              <Input required value={regForm.name}
                onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                placeholder="Ex: ASVO FC" data-testid="reg-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ville</Label>
                <Input value={regForm.city}
                  onChange={(e) => setRegForm({ ...regForm, city: e.target.value })}
                  placeholder="Cotonou" data-testid="reg-city" />
              </div>
              <div>
                <Label>Pays</Label>
                <Select value={regForm.country} onValueChange={(v) => setRegForm({ ...regForm, country: v })}>
                  <SelectTrigger data-testid="reg-country"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WEST_AFRICA_COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Sport</Label>
              <Select value={regForm.sport} onValueChange={(v) => setRegForm({ ...regForm, sport: v })}>
                <SelectTrigger data-testid="reg-sport"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Football", "Basketball", "Handball", "Volleyball", "Athlétisme", "Rugby", "Autre"].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Code Président *</Label>
              <Input type="text" required minLength={4}
                value={regForm.admin_code}
                onChange={(e) => setRegForm({ ...regForm, admin_code: e.target.value })}
                placeholder="Min. 4 caractères" data-testid="reg-admincode" />
              <p className="text-xs text-[#616161] mt-1">Ce code servira pour votre 1ère connexion.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRegisterOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={loading}
                className="bg-[#0A6B3A] hover:bg-[#08512C] text-white" data-testid="reg-submit-btn">
                {loading ? "Création..." : "Créer mon club"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
